import React, { ReactElement, useMemo, useState } from 'react';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { ChevronRight, ClipboardCheck, ClipboardList, Hourglass } from 'lucide-react';
import { MembershipEntity, MembershipStatus, SprintEntity } from '@data';
import { useListPmMembers } from '@data/hooks/membership';
import { useListSprints } from '@data/hooks/sprints';
import { useOrganizationId } from '@app/utils/hooks';
import { dayKey, fullDate, getCurrentSprint } from '@app/utils';
import { getEndedSprints } from '@app/utils/helpers/sprint-utils';
import { Roles } from '@app/utils/types';
import { shortfallsOf } from '@app/utils/helpers/shortfalls';
import { Card, RingGauge, ScrollFade, SectionLabel } from '@app/components/core';
import { Loader } from '@ui';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@ui/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
  selectItemsByValue,
} from '@ui/components/ui/select';
import { SprintsToCloseCard } from './SprintsToCloseCard';
import { AwaitingApprovalCard } from './AwaitingApprovalCard';
import { TimePerProductCard } from './TimePerProductCard';
import { RevenuePerProductCard } from './RevenuePerProductCard';
import { InvolvementsCard } from './InvolvementsCard';
import { HoursPerProductCard } from './HoursPerProductCard';
import { QUARTER_COUNT, trailingQuarters, useProductHoursByWindow } from './portfolioHours';
import { ProductHours } from './productTime';

/** A person as these cards need them. Everyone is held to the same day, so there is no contract to carry. */
type Person = { id: string; name: string };

/** One working day of a sprint: how it is labelled, where it sits relative to today, and who filed nothing. */
type SprintDay = {
  iso: string;
  weekday: string;
  dateLabel: string;
  isFuture: boolean;
  names: Person[];
};

const toPerson = (m: MembershipEntity): Person => ({
  id: m.id,
  name: `${m.user.firstName ?? ''} ${m.user.lastName ?? ''}`.trim() || m.user.email || '—',
});
const byName = (a: Person, b: Person): number => a.name.localeCompare(b.name);
/** Clients hold memberships too but never report time, so they are never part of these figures. */
const employees = (members?: MembershipEntity[]): MembershipEntity[] =>
  (members ?? []).filter(m => m.role !== Roles.client);

/**
 * The working days (Mon–Fri) an inclusive 'yyyy-MM-dd' range spans, as day keys. Walked in UTC so the keys
 * line up with the ones the API reports empty days under, whatever timezone the viewer is in.
 */
const eachWeekday = (fromIso: string, toIso: string): string[] => {
  const days: string[] = [];
  const day = new Date(`${fromIso}T00:00:00Z`);
  const end = new Date(`${toIso}T00:00:00Z`);
  while (day <= end) {
    if (![0, 6].includes(day.getUTCDay())) days.push(day.toISOString().slice(0, 10));
    day.setUTCDate(day.getUTCDate() + 1);
  }

  return days;
};
const atUtc = (iso: string): Date => new Date(`${iso}T00:00:00Z`);

/**
 * The reporting row of the Insights tab: who is behind on filing. Missed days over the sprint running now
 * (the ring, the last working day's list, and the whole sprint behind them) beside the hours people owe over
 * the sprint that closed before it.
 */
const InsightsPanel = (): ReactElement => {
  const { t, i18n } = useTranslation();
  const organizationId = useOrganizationId() ?? '';

  const { data: sprintsData } = useListSprints({ limit: 200 });
  const sortedSprints = useMemo(
    () =>
      [...(sprintsData?.items ?? [])].sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime()),
    [sprintsData],
  );

  const todayIso = dayKey(new Date());
  const currentSprint = useMemo(() => getCurrentSprint(sortedSprints), [sortedSprints]);

  // Only ended sprints can owe hours.
  const closedSprints = useMemo(() => getEndedSprints(sortedSprints, todayIso), [sortedSprints, todayIso]);
  const [pickedSprintId, setPickedSprintId] = useState<string | null>(null);
  const shortfallSprint = closedSprints.find(s => s.id === pickedSprintId) ?? closedSprints[0];

  // Reporting across the current sprint: per member, the working days they filed nothing on. The API caps the
  // window at today, so days still ahead never come back as gaps.
  const { data: sprintMembersData, isLoading: membersLoading } = useListPmMembers(
    organizationId,
    { from: currentSprint?.startDate, to: currentSprint?.endDate, userStatus: MembershipStatus.active },
    !!currentSprint,
  );
  const sprintMembers = useMemo(() => employees(sprintMembersData?.items), [sprintMembersData]);

  // The most recent working day (weekends skipped): people report at end of day, so today is almost always
  // incomplete — the last working day is the report a manager can actually act on.
  const lastWorkingDayIso = useMemo(() => {
    const day = atUtc(todayIso);
    do {
      day.setUTCDate(day.getUTCDate() - 1);
    } while ([0, 6].includes(day.getUTCDay()));

    return day.toISOString().slice(0, 10);
  }, [todayIso]);
  const lastWorkingDayLabel = new Intl.DateTimeFormat(i18n.language, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  }).format(atUtc(lastWorkingDayIso));
  // Over a weekend the day being reported on is not yesterday any more, so it gets named instead. Saying
  // "yesterday" on a Monday points at a Sunday nobody was asked to report.
  const lastWorkingDayWasYesterday = useMemo(() => {
    const yesterday = atUtc(todayIso);
    yesterday.setUTCDate(yesterday.getUTCDate() - 1);

    return yesterday.toISOString().slice(0, 10) === lastWorkingDayIso;
  }, [todayIso, lastWorkingDayIso]);
  const lastWorkingDayName = new Intl.DateTimeFormat(i18n.language, { weekday: 'long', timeZone: 'UTC' }).format(
    atUtc(lastWorkingDayIso),
  );
  // Asked for as its own one-day window, so the list still holds on the first day of a sprint, when the last
  // working day belongs to the previous one. Bounded in UTC because that is the calendar the API keys days on.
  const lastWorkingDayWindow = useMemo(
    () => ({
      from: `${lastWorkingDayIso}T00:00:00.000Z`,
      to: `${lastWorkingDayIso}T23:59:59.999Z`,
      userStatus: MembershipStatus.active,
    }),
    [lastWorkingDayIso],
  );
  const { data: lastDayMembersData } = useListPmMembers(organizationId, lastWorkingDayWindow);
  // A one-day window, so any empty day at all means they filed nothing that day.
  const notReported = useMemo(
    () =>
      employees(lastDayMembersData?.items)
        .filter(m => m.emptyDaysCount > 0)
        .map(toPerson)
        .sort(byName),
    [lastDayMembersData],
  );

  // Every working day of the sprint with who filed nothing on it — the card's list widened to the whole
  // sprint, shown in the dialog.
  const [sprintOpen, setSprintOpen] = useState(false);
  const sprintDays = useMemo<SprintDay[]>(() => {
    if (!currentSprint) return [];
    const weekday = new Intl.DateTimeFormat(i18n.language, { weekday: 'short', timeZone: 'UTC' });
    const dateLabel = new Intl.DateTimeFormat(i18n.language, { day: 'numeric', month: 'short', timeZone: 'UTC' });

    return eachWeekday(fullDate(currentSprint.startDate), fullDate(currentSprint.endDate)).map(iso => ({
      iso,
      weekday: weekday.format(atUtc(iso)),
      dateLabel: dateLabel.format(atUtc(iso)),
      isFuture: iso > todayIso,
      names: sprintMembers
        .filter(m => m.emptyDays?.includes(iso))
        .map(toPerson)
        .sort(byName),
    }));
  }, [currentSprint, sprintMembers, i18n.language, todayIso]);

  const rangeLabel = (sprint?: SprintEntity): string => {
    if (!sprint) return '';
    const format = new Intl.DateTimeFormat(i18n.language, { day: 'numeric', month: 'short', timeZone: 'UTC' });

    return `${format.format(atUtc(fullDate(sprint.startDate)))} – ${format.format(atUtc(fullDate(sprint.endDate)))}`;
  };
  // Sprint numbers restart each year, so the menu groups by year and the trigger names an earlier one.
  const sprintLabel = (sprint: SprintEntity, range: string): ReactElement => (
    <span className="flex items-baseline gap-2">
      {t('missingHoursSprint', { number: sprint.number })}
      <span className="text-muted-foreground">{range}</span>
    </span>
  );
  const sprintYears = [...new Set(closedSprints.map(sprint => fullDate(sprint.startDate).slice(0, 4)))].map(year => ({
    year,
    sprints: closedSprints.filter(sprint => fullDate(sprint.startDate).startsWith(year)),
  }));
  const triggerLabels = selectItemsByValue(
    closedSprints.map(sprint => {
      const year = fullDate(sprint.startDate).slice(0, 4);
      const range = year === todayIso.slice(0, 4) ? rangeLabel(sprint) : `${rangeLabel(sprint)}, ${year}`;

      return { value: sprint.id, label: sprintLabel(sprint, range) };
    }),
  );

  // The ring counts people, not reports: how many have at least one missing day in the sprint so far. Today
  // is excluded — people report at end of day, so a gap today is not a gap yet.
  const withGaps = useMemo(
    () => sprintMembers.filter(m => m.emptyDays?.some(day => day < todayIso)).length,
    [sprintMembers, todayIso],
  );
  const gapShare = sprintMembers.length ? Math.round((withGaps / sprintMembers.length) * 100) : 0;

  // Missing hours over the last closed sprint: eight hours for every working day it held, minus what was
  // logged. Every working day counts, filed or not — over a sprint that has ended, a day nobody reported is
  // hours owed just as surely as a day reported half full. Only a closed sprint can be judged this way:
  // mid-sprint the same sum would charge people for days that simply are not finished yet.
  const { data: shortfallMembersData } = useListPmMembers(
    organizationId,
    { from: shortfallSprint?.startDate, to: shortfallSprint?.endDate, userStatus: MembershipStatus.active },
    !!shortfallSprint,
  );
  // One stable array per render, keyed on the month, so both cards below total the same window.
  const now = new Date();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const quarters = useMemo(() => trailingQuarters(now, QUARTER_COUNT), [now.getFullYear(), now.getMonth()]);
  const {
    byWindow: byQuarter,
    isLoading: portfolioLoading,
    isError: portfolioFailed,
  } = useProductHoursByWindow(organizationId, quarters);
  const periodLabel = `${quarters[0].label} – ${quarters[quarters.length - 1].label}`;

  // Summed from the quarters rather than fetched again, so the totals cannot disagree with them.
  const portfolioTotals = useMemo<ProductHours[]>(() => {
    const byProduct = new Map<string, ProductHours>();

    // Oldest first, so a renamed or reclassified product reads as it is now. Only hours accumulate.
    byQuarter.flat().forEach(p => {
      byProduct.set(p.id, { ...p, hours: (byProduct.get(p.id)?.hours ?? 0) + p.hours });
    });

    return [...byProduct.values()];
  }, [byQuarter]);

  const shortfalls = useMemo(() => {
    if (!shortfallSprint) return [];
    const workingDays = eachWeekday(fullDate(shortfallSprint.startDate), fullDate(shortfallSprint.endDate)).length;

    return shortfallsOf(
      employees(shortfallMembersData?.items).map(m => ({ ...toPerson(m), totalMinutes: m.totalMinutes })),
      workingDays,
    );
  }, [shortfallSprint, shortfallMembersData]);

  if (membersLoading) {
    return (
      <div className="flex justify-center py-20">
        <Loader />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <section>
        <SectionLabel>{t('sectionReporting')}</SectionLabel>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {/* Reporting gaps — the whole card is a button into the full-sprint view. */}
          <button type="button" onClick={() => setSprintOpen(true)} className="text-left">
            <Card className="flex h-full cursor-pointer flex-col p-6 transition-shadow hover:shadow-md">
              <div className="mb-4 flex items-center justify-between gap-3">
                <p className="flex items-center gap-2 text-base font-semibold text-foreground">
                  <ClipboardCheck className="size-4 text-muted-foreground" />
                  {t('reportingGaps')}
                </p>
                <span className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground">
                  {t('openSprintView')}
                  <ChevronRight className="size-3.5" />
                </span>
              </div>
              <div className="flex flex-1 items-center gap-6">
                <div className="flex shrink-0 flex-col items-center">
                  <RingGauge
                    percent={gapShare}
                    value={`${withGaps}`}
                    color="var(--primary)"
                    caption={t('ofNTotal', { count: sprintMembers.length })}
                  />
                  <span className="mt-2 max-w-32 text-center text-[11px] leading-tight text-muted-foreground">
                    {t('missedADayThisSprint')}
                  </span>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="mb-2 flex flex-wrap items-baseline justify-between gap-x-2">
                    <span className="text-xs font-medium text-foreground">
                      {lastWorkingDayWasYesterday
                        ? t('notReportedYesterday')
                        : t('notReportedOnDay', { day: lastWorkingDayName })}
                    </span>
                    <span className="shrink-0 text-xs text-muted-foreground">{lastWorkingDayLabel}</span>
                  </div>
                  {notReported.length ? (
                    // 44 fits five rows whole. At 40 the fifth was cut by 6px, which faded as if a sixth
                    // person were below. Keep in step with the list in the card beside this one.
                    <ScrollFade
                      className="max-h-44 rounded-xl border border-border"
                      fadeClassName="from-card rounded-b-xl"
                    >
                      <ul className="divide-y divide-border">
                        {notReported.map(p => (
                          <li key={p.id} className="truncate px-3 py-1.5 text-sm font-medium text-foreground">
                            {p.name}
                          </li>
                        ))}
                      </ul>
                    </ScrollFade>
                  ) : (
                    <p className="rounded-xl border border-dashed border-border py-6 text-center text-sm text-muted-foreground">
                      {lastWorkingDayWasYesterday
                        ? t('everyoneReportedYesterday')
                        : t('everyoneReportedOnDay', { day: lastWorkingDayName })}
                    </p>
                  )}
                </div>
              </div>
            </Card>
          </button>

          {/* Who owes hours over a closed sprint, the one before this by default. */}
          <Card className="flex h-full flex-col p-6">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <p className="flex items-center gap-2 text-base font-semibold text-foreground">
                <Hourglass className="size-4 text-muted-foreground" />
                {t('missingHours')}
              </p>
              {shortfallSprint && (
                <Select
                  value={shortfallSprint.id}
                  onValueChange={next => next && setPickedSprintId(next)}
                  items={triggerLabels}
                >
                  {/* Wide enough for a menu row, so the menu, which takes the trigger's width, never wraps. */}
                  <SelectTrigger className="min-w-56 text-foreground shadow-xs" aria-label={t('sprint')}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-72" align="end" alignItemWithTrigger={false}>
                    {sprintYears.map(({ year, sprints }) => (
                      <SelectGroup key={year}>
                        {sprintYears.length > 1 && <SelectLabel>{year}</SelectLabel>}
                        {sprints.map(sprint => (
                          <SelectItem key={sprint.id} value={sprint.id} className="whitespace-nowrap">
                            {sprintLabel(sprint, rangeLabel(sprint))}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {shortfalls.length ? (
              // Built like the list beside it: column names as a caption line, then the rows in their own
              // bordered box. The two cards sit side by side and read as one row, so a bare table here
              // against a boxed list there made the same kind of thing look like two different things.
              <>
                <div className="mb-2 flex items-baseline justify-between gap-2">
                  <span className="truncate text-xs font-medium text-foreground">{t('member')}</span>
                  <span className="shrink-0 text-xs text-muted-foreground">{t('colMissing')}</span>
                </div>
                <ScrollFade className="max-h-44 rounded-xl border border-border" fadeClassName="from-card rounded-b-xl">
                  <table className="w-full text-sm">
                    <tbody className="divide-y divide-border">
                      {shortfalls.map(p => (
                        <tr key={p.id}>
                          <td className="truncate px-3 py-1.5 font-medium text-foreground">{p.name}</td>
                          <td className="w-20 px-3 py-1.5 text-right tabular-nums text-foreground">
                            {t('nHours', { hours: p.missing })}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </ScrollFade>
              </>
            ) : (
              <p className="flex flex-1 items-center justify-center rounded-xl border border-dashed border-border py-6 text-center text-sm text-muted-foreground">
                {t('everyoneLoggedFullDays')}
              </p>
            )}
          </Card>
        </div>
      </section>

      {/* 2. PRODUCTS — the reporting chain in order: the sprints still to close, then the closed ones
          waiting on a client. */}
      <section>
        <SectionLabel>{t('sectionProducts')}</SectionLabel>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <SprintsToCloseCard />
          <AwaitingApprovalCard />
        </div>
      </section>

      {/* 3. TIME — where the year's hours actually went, once the reporting chain above is in order: how
          each product's time accumulated sprint by sprint, then what the portfolio totalled over the
          trailing quarters — charged against not charged, and each product against the average. */}
      <section className="space-y-4">
        <SectionLabel>{t('sectionTime')}</SectionLabel>
        <TimePerProductCard />
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
          <InvolvementsCard
            products={portfolioTotals}
            isLoading={portfolioLoading}
            isError={portfolioFailed}
            periodLabel={periodLabel}
          />
          {/* A bar per product, each with its name under it, so the plot takes two thirds and the table one. */}
          <div className="xl:col-span-2">
            <HoursPerProductCard
              products={portfolioTotals}
              isLoading={portfolioLoading}
              isError={portfolioFailed}
              periodLabel={periodLabel}
            />
          </div>
        </div>
      </section>

      {/* 4. MONEY — the year in euros rather than hours: what each product actually brought in. */}
      <section className="space-y-4">
        <SectionLabel>{t('sectionMoney')}</SectionLabel>
        <RevenuePerProductCard />
      </section>

      {/* The sprint in full — one column per working day across the two weeks. */}
      <Dialog open={sprintOpen} onOpenChange={setSprintOpen}>
        <DialogContent className="flex h-[90vh] flex-col sm:max-w-[95vw]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="size-4" />
              {t('notReportedThisSprint')}
            </DialogTitle>
            <DialogDescription>{rangeLabel(currentSprint)}</DialogDescription>
          </DialogHeader>
          <div className="grid min-h-0 flex-1 grid-cols-2 gap-3 overflow-y-auto sm:grid-cols-5 lg:grid-rows-2">
            {sprintDays.map(d => (
              <div
                key={d.iso}
                className={cx(
                  'flex min-h-0 flex-col overflow-hidden rounded-xl border border-border',
                  d.isFuture && 'border-dashed bg-muted/40 opacity-60',
                )}
              >
                <div className="border-b border-border px-3 py-2">
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{d.weekday}</p>
                  <p className="text-[11px] text-muted-foreground/70">{d.dateLabel}</p>
                </div>
                {(() => {
                  if (d.isFuture) {
                    return (
                      <p className="flex flex-1 items-center justify-center px-3 py-6 text-center text-xs text-muted-foreground/60">
                        {t('upcomingDay')}
                      </p>
                    );
                  }
                  if (d.names.length) {
                    return (
                      // A rule under every name, the last one included: the column is taller than the list,
                      // so a name with nothing ruled off beneath it floats in the empty space.
                      <ul className="min-h-0 flex-1 overflow-y-auto">
                        {d.names.map(n => (
                          <li
                            key={n.id}
                            className="truncate border-b border-border px-3 py-2 text-sm font-medium text-foreground"
                          >
                            {n.name}
                          </li>
                        ))}
                      </ul>
                    );
                  }

                  return (
                    <p className="flex flex-1 items-center justify-center px-3 py-6 text-center text-xs text-muted-foreground">
                      {t('everyoneReported')}
                    </p>
                  );
                })()}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InsightsPanel;
